import {
  useLazyInvoiceLineItemsListSelectedQuery,
  useLazyInvoicePaymentsListSelectedQuery,
  useLazyInvoiceRefundsListSelectedQuery,
  useLazyInvoicesExportRetrieveQuery,
} from '@bambi/api-client/bambi';
import type { InvoicesExportRetrieveApiArg } from '@bambi/api-client/schemas';
import { useCallback, useState } from 'react';

import type { InvoiceExportOrdering } from './invoiceOrderingOptions';
import { useAppDispatch } from 'src/app/store/hooks';
import { saveFile } from 'src/common/util/saveFile';
import formatServerError from 'src/common/util/serverErrorFormatter';
import {
  setExportedCondensedReportData,
  setExportedInvoicePaymentsData,
  setExportedInvoiceRefundsData,
} from 'src/features/billing/invoices/invoice.slice';
import { parseInvoiceExport } from 'src/features/billing/invoices/parseInvoiceExport';
import type {
  InvoiceExportCondensedReport,
  InvoicePaymentRow,
  InvoiceRefundRow,
} from 'src/features/billing/invoices/types';

// 'csv' saves the export as downloaded; 'pdf' parses it into the rows the
// condensed report renders.
type ExportFormat = 'csv' | 'pdf';

export type InvoiceExportCSVArg = {
  rangeStart: string;
  rangeEnd?: string;
  invoice?: string;
  tripPayerIn?: string[];
  outputType?: InvoicesExportRetrieveApiArg['outputType'];
  format: ExportFormat;
  overallPaymentStatusIn?: (
    | 'overpaid'
    | 'paid'
    | 'partially_paid'
    | 'unknown'
    | 'unpaid'
  )[];
  ordering?: InvoiceExportOrdering;
};

export type InvoiceSelectedListExportCSVArg = {
  selectedInvoices: string[];
  format: ExportFormat;
  outputType?: 'quickbooks' | 'condensed';
  ordering?: InvoiceExportOrdering;
};

const parseExport = async (file: File) =>
  parseInvoiceExport(await file.text(), file.name);

export function useLazyFetchInvoiceExportData() {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<InvoiceExportCondensedReport>();
  const [fetchInvoiceExport] = useLazyInvoicesExportRetrieveQuery();
  const [fetchInvoiceExportByInvoiceId] =
    useLazyInvoiceLineItemsListSelectedQuery();
  const [fetchInvoicePaymentsExport] =
    useLazyInvoicePaymentsListSelectedQuery();
  const [fetchInvoiceRefundsExport] = useLazyInvoiceRefundsListSelectedQuery();

  // Fetch related data by invoice id
  const fetchRelatedData = useCallback(
    async (invoiceIds: string[]) => {
      const [payments, refunds] = await Promise.all([
        fetchInvoicePaymentsExport({
          invoicePaymentExportFilter: { selected_invoices: invoiceIds },
        }).unwrap(),
        fetchInvoiceRefundsExport({
          invoicesListFilter: { selected_invoices: invoiceIds },
        }).unwrap(),
      ]);

      return {
        payments: await parseExport(payments),
        refunds: await parseExport(refunds),
      };
    },
    [fetchInvoicePaymentsExport, fetchInvoiceRefundsExport]
  );

  // Fetch invoice data by query or by selected
  const trigger = useCallback(
    async (arg: InvoiceExportCSVArg | InvoiceSelectedListExportCSVArg) => {
      setLoading(true);
      setData(undefined);
      setError(undefined);

      try {
        // The backend's default is also quickbooks; sent explicitly so the
        // request reads the same as before this hook used the generated client.
        const outputType = arg.outputType ?? 'quickbooks';
        const file = await (
          'selectedInvoices' in arg
            ? fetchInvoiceExportByInvoiceId({
                outputType,
                ordering: arg.ordering,
                invoicesListFilter: { selected_invoices: arg.selectedInvoices },
              })
            : fetchInvoiceExport({
                rangeStart: arg.rangeStart,
                rangeEnd: arg.rangeEnd,
                invoice: arg.invoice,
                tripPayerIn: arg.tripPayerIn,
                outputType,
                ordering: arg.ordering,
                overallPaymentStatusIn: arg.overallPaymentStatusIn?.length
                  ? arg.overallPaymentStatusIn.join(',')
                  : undefined,
              })
        ).unwrap();

        if (arg.format === 'csv') {
          saveFile(file, file.name);
          return;
        }

        const invoiceExport = await parseExport(file);

        if (!invoiceExport.records.length) {
          setError('No invoices found');
          return;
        }

        const invoiceIds = invoiceExport.records.map(
          (record) => record.InvoiceID
        );
        const relatedDataResults = await fetchRelatedData(invoiceIds);

        setData(invoiceExport as InvoiceExportCondensedReport);

        dispatch(
          setExportedCondensedReportData(
            invoiceExport as InvoiceExportCondensedReport
          )
        );
        dispatch(
          setExportedInvoicePaymentsData(
            relatedDataResults.payments.records as InvoicePaymentRow[]
          )
        );
        dispatch(
          setExportedInvoiceRefundsData(
            relatedDataResults.refunds.records as InvoiceRefundRow[]
          )
        );
      } catch (e) {
        const errMsg = `There was an error downloading the data. ${formatServerError(
          e
        )}`;
        setError(errMsg);
      } finally {
        setLoading(false);
      }
    },
    [
      dispatch,
      fetchInvoiceExport,
      fetchInvoiceExportByInvoiceId,
      fetchRelatedData,
    ]
  );

  return {
    loading,
    error,
    trigger,
    data,
  };
}
